<template>
  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 scale-95"
    enter-to-class="opacity-100 scale-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-95"
  >
    <div v-if="modelValue" class="fixed inset-0 z-[1500] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="$emit('update:modelValue', false)"></div>
      
      <!-- Modal Content -->
      <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div class="bg-red-600 p-6 text-white flex justify-between items-start">
          <div>
            <h3 class="text-xl font-bold mb-1">Product Enquiry</h3>
            <p class="text-xs text-red-100">{{ productName }}</p>
          </div>
          <button @click="$emit('update:modelValue', false)" class="text-red-100 hover:text-white transition-colors">
            <component :is="XIcon" class="w-5 h-5" />
          </button>
        </div>
        
        <div class="p-6 space-y-4">
          <div class="space-y-4">
            <div>
              <label class="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 ml-1">Your Name</label>
              <input v-model="form.name" type="text" class="w-full h-11 px-4 rounded-lg bg-slate-50 border border-slate-100 text-sm font-medium text-slate-900 focus:bg-white focus:border-red-600 focus:outline-none focus:ring-4 focus:ring-red-600/5 transition-all" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 ml-1">Phone Number</label>
                <input v-model="form.phone" type="tel" class="w-full h-11 px-4 rounded-lg bg-slate-50 border border-slate-100 text-sm font-medium text-slate-900 focus:bg-white focus:border-red-600 focus:outline-none focus:ring-4 focus:ring-red-600/5 transition-all" />
              </div>
              <div>
                <label class="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 ml-1">Email (Optional)</label>
                <input v-model="form.email" type="email" class="w-full h-11 px-4 rounded-lg bg-slate-50 border border-slate-100 text-sm font-medium text-slate-900 focus:bg-white focus:border-red-600 focus:outline-none focus:ring-4 focus:ring-red-600/5 transition-all" />
              </div>
            </div>
          </div>
          
          <div v-if="success" class="p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs font-bold text-center">
            Enquiry submitted! We will contact you shortly.
          </div>

          <button 
            v-else
            @click="submitLead" 
            :disabled="loading || !form.name || !form.phone"
            class="w-full h-11 mt-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 disabled:opacity-50"
          >
            <span v-if="!loading">Send Enquiry</span>
            <div v-else class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { X as XIcon } from 'lucide-vue-next'
import { useTrackingStore } from '~/stores/tracking'
import { useNuxtApp } from '#imports'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const props = defineProps<{
  modelValue: boolean
  productName: string
}>()

const emit = defineEmits(['update:modelValue'])

const trackingStore = useTrackingStore()
const loading = ref(false)
const success = ref(false)

const form = reactive({
  name: '',
  phone: '',
  email: ''
})

const submitLead = async () => {
  if (!form.name || !form.phone) return
  loading.value = true
  
  try {
    const { $db } = useNuxtApp() as any
    const enquiriesCol = collection($db, 'enquiries')
    
    // Save to dedicated Enquiries collection
    await addDoc(enquiriesCol, {
      name: form.name,
      phone: form.phone,
      email: form.email || null,
      interestedProduct: props.productName,
      status: 'Pending',
      createdAt: serverTimestamp(),
      source: 'Product Enquiry Modal',
      url: window.location.href,
      notes: []
    })

    // Also capture as a Lead for global tracking analytics
    await trackingStore.captureLead({
      name: form.name,
      phone: form.phone,
      email: form.email,
      source: 'Product Enquiry',
      interestedProduct: props.productName
    })
    
    trackingStore.addScore(25, 'Product Enquiry: ' + props.productName)

    success.value = true
    setTimeout(() => {
      emit('update:modelValue', false)
      success.value = false
      form.name = ''
      form.phone = ''
      form.email = ''
    }, 2000)
  } catch (error) {
    console.error('Lead capture error', error)
  } finally {
    loading.value = false
  }
}
</script>
